import assertNever from "assert-never";
import { LocalDateTime } from "@js-joda/core";
import React, { useEffect, useState } from "react";
import { NursingQuestionId } from "../../../messages/ObjectIds";
import {
  VitalExtraDetails,
  VitalMethodNames,
  VitalMethodType,
  VitalType as VitalTypeQuestions,
  VitalUnitNames,
  VitalUnitType,
} from "../../../messages/Vital";
import { RequiredDocAnswerableQuestionProps } from "../../RequiredDocAnswerableQuestion";
import RequiredDocVitalSign from "./RequiredDocVitalSign";

export type VitalType = "BLOOD_PRESSURE" | "REGULAR_NUMBER";
export type VitalAnswerType = "BASE_ANSWER" | "MEASURED_AT" | "METHOD" | "COMMENTS" | "UNIT";

interface Props extends RequiredDocAnswerableQuestionProps {
  questionText: string;
  vitalMeasuredAt: LocalDateTime | null;
  vitalMethod: VitalMethodType | null;
  vitalComments: string | null;
  vitalUnit: VitalUnitType | null;
  readonly: boolean;
  vitalType: VitalType;
  nursingQuestionId: NursingQuestionId;
}

const RequiredDocVitalSignActivity = ({
  questionId,
  questionText,
  currentAnswer,
  isRequired,
  isValid,
  vitalMeasuredAt,
  vitalMethod,
  vitalComments,
  vitalUnit,
  readonly,
  notes,
  vitalType,
  onClickWhileDisabled,
  nursingQuestionId,
  handleOnInsertAnswer,
}: Props) => {
  const [draftVitalMethod, setVitalMethod] = useState<null | VitalMethodType>(null);
  const [draftVitalMeasuredAt, setVitalMeasuredAt] = useState<null | LocalDateTime>(null);
  const [draftVitalComments, setVitalComments] = useState<null | string>(null);
  const [draftVitalUnit, setVitalUnit] = useState<null | VitalUnitType>(null);

  useEffect(() => {
    setVitalMethod(vitalMethod);
    setVitalMeasuredAt(vitalMeasuredAt);
    setVitalComments(vitalComments);
    setVitalUnit(vitalUnit);
  }, [vitalMethod, vitalMeasuredAt, vitalComments, vitalUnit]);

  const handleClickWhileDisabled = React.useCallback(() => {
    if (readonly && onClickWhileDisabled !== undefined) {
      onClickWhileDisabled();
    }
  }, [readonly, onClickWhileDisabled]);

  const handleVitalAnswerChange = React.useCallback(
    (id: number, answer: string | string[] | null, vitalAnswerType: VitalAnswerType) => {
      let questionBaseAnswer = currentAnswer === undefined ? null : currentAnswer;
      const vitalExtraDetails: VitalExtraDetails = {
        method: draftVitalMethod,
        measuredAt: draftVitalMeasuredAt,
        comments: draftVitalComments,
        unit: draftVitalUnit,
      };

      switch (vitalAnswerType) {
        case "BASE_ANSWER": {
          questionBaseAnswer = answer;
          handleOnInsertAnswer(id, questionBaseAnswer, vitalExtraDetails);
          break;
        }

        case "METHOD": {
          vitalExtraDetails.method = answer as VitalMethodType;
          handleOnInsertAnswer(id, questionBaseAnswer, vitalExtraDetails);
          break;
        }
        case "UNIT": {
          vitalExtraDetails.unit = answer as VitalUnitType;
          handleOnInsertAnswer(id, questionBaseAnswer, vitalExtraDetails);
          break;
        }

        case "MEASURED_AT": {
          const measuredAt = typeof answer === "string" ? LocalDateTime.parse(answer) : null;
          vitalExtraDetails.measuredAt = measuredAt === null ? null : measuredAt;
          handleOnInsertAnswer(id, questionBaseAnswer, vitalExtraDetails);
          break;
        }

        case "COMMENTS": {
          vitalExtraDetails.comments = typeof answer === "string" ? answer : null;
          handleOnInsertAnswer(id, questionBaseAnswer, vitalExtraDetails);
          break;
        }

        default: {
          assertNever(vitalAnswerType);
        }
      }
    },
    [
      currentAnswer,
      draftVitalComments,
      draftVitalMeasuredAt,
      draftVitalMethod,
      draftVitalUnit,
      handleOnInsertAnswer,
    ]
  );

  const getVitalMethodKeyFromAnswer = React.useMemo(
    () =>
      (answer: string): VitalMethodType | null => {
        for (const key of Array.from(VitalMethodNames.keys())) {
          if (VitalMethodNames.get(key) === answer) {
            return key;
          }
        }
        return null;
      },
    []
  );

  const getVitalUnitKeyFromAnswer = React.useMemo(
    () =>
      (answer: string): VitalUnitType | null => {
        for (const key of Array.from(VitalUnitNames.keys())) {
          if (VitalUnitNames.get(key) === answer) {
            return key;
          }
        }
        return null;
      },
    []
  );

  const handleInsertVitalMethod = React.useCallback(
    (id: number, answer: string | string[] | null) => {
      if (typeof answer === "string") {
        const method = getVitalMethodKeyFromAnswer(answer);
        if (method !== null) {
          setVitalMethod(method);
          handleVitalAnswerChange(id, method, "METHOD");
        }
      }
    },
    [getVitalMethodKeyFromAnswer, handleVitalAnswerChange]
  );

  const handleInsertVitalMeasureAt = React.useCallback(
    (id: number, answer: string | string[] | null) => {
      if (typeof answer === "string") {
        setVitalMeasuredAt(LocalDateTime.parse(answer));
      }
      handleVitalAnswerChange(id, answer, "MEASURED_AT");
    },
    [handleVitalAnswerChange]
  );

  const handleInsertVitalComments = React.useCallback(
    (id: number, answer: string | string[] | null) => {
      if (typeof answer === "string") {
        setVitalComments(answer);
      }
      handleVitalAnswerChange(id, answer, "COMMENTS");
    },
    [handleVitalAnswerChange]
  );

  const handleInsertVitalUnit = React.useCallback(
    (id: number, answer: string | string[] | null) => {
      if (typeof answer === "string") {
        const unit = getVitalUnitKeyFromAnswer(answer);
        setVitalUnit(unit);
        handleVitalAnswerChange(id, unit, "UNIT");
      }
    },
    [getVitalUnitKeyFromAnswer, handleVitalAnswerChange]
  );

  const getMethodsByNursingQuestionId = React.useCallback((): string[] => {
    switch (nursingQuestionId) {
      case VitalTypeQuestions.bloodPressure:
        return [
          VitalMethodNames.get(VitalMethodType.RightArm) as string,
          VitalMethodNames.get(VitalMethodType.LeftArm) as string,
          VitalMethodNames.get(VitalMethodType.Sitting) as string,
          VitalMethodNames.get(VitalMethodType.Laying) as string,
          VitalMethodNames.get(VitalMethodType.Standing) as string,
        ];
      case VitalTypeQuestions.pulseRate:
        return [
          VitalMethodNames.get(VitalMethodType.Regular) as string,
          VitalMethodNames.get(VitalMethodType.Irregular) as string,
        ];
      case VitalTypeQuestions.respirationRate:
        return [VitalMethodNames.get(VitalMethodType.Regular) as string];
      case VitalTypeQuestions.bodyTemperature:
        return [
          VitalMethodNames.get(VitalMethodType.Oral) as string,
          VitalMethodNames.get(VitalMethodType.Axillary) as string,
          VitalMethodNames.get(VitalMethodType.Rectal) as string,
          VitalMethodNames.get(VitalMethodType.RemoteForehead) as string,
        ];
      default:
        return [];
    }
  }, [nursingQuestionId]);

  const getUnitsByNursingQuestionId = React.useCallback((): string[] => {
    switch (nursingQuestionId) {
      case VitalTypeQuestions.bloodPressure:
        return [VitalUnitNames.get(VitalUnitType.MMHG) as string];
      case VitalTypeQuestions.pulseRate:
        return [VitalUnitNames.get(VitalUnitType.BeatsPerMinute) as string];
      case VitalTypeQuestions.respirationRate:
        return [VitalUnitNames.get(VitalUnitType.BreathPerMinute) as string];
      case VitalTypeQuestions.bodyTemperature:
        return [VitalUnitNames.get(VitalUnitType.FAHRENHEIT) as string];
      default:
        return [];
    }
  }, [nursingQuestionId]);

  return (
    <RequiredDocVitalSign
      methodTypeOptions={getMethodsByNursingQuestionId()}
      unitTypeOptions={getUnitsByNursingQuestionId()}
      questionId={questionId}
      isRequired={isRequired}
      isValid={isValid}
      notes={notes}
      questionText={questionText}
      prefilledAnswer={null}
      readonly={readonly}
      currentAnswer={currentAnswer}
      vitalType={vitalType}
      vitalComments={draftVitalComments}
      vitalMeasuredAt={draftVitalMeasuredAt}
      vitalMethod={draftVitalMethod}
      vitalUnit={draftVitalUnit}
      onVitalAnswerChange={handleVitalAnswerChange}
      onInsertVitalMethod={handleInsertVitalMethod}
      onInsertVitalUnit={handleInsertVitalUnit}
      onInsertVitalComments={handleInsertVitalComments}
      onInsertVitalMeasuredAt={handleInsertVitalMeasureAt}
      handleOnInsertAnswer={handleOnInsertAnswer}
      handleClickWhileDisabled={handleClickWhileDisabled}
    />
  );
};

export default React.memo(RequiredDocVitalSignActivity);
